import { Component } from "@mzw/component";
import { generateStableId } from "@mzw/utilities";

export class TOC extends Component {
	static tagName = "table-of-contents";
	static {
		customElements.define(TOC.tagName, TOC);
	}

	/** @type Boolean */ #nested;
	/** @type Boolean */ #after;
	/** @type String */ #level;
	/** @type String | null */ #heading;
	/** @type String | null */ #headingType;
	/** @type String */ #target;
	/** @type String */ #scrollTarget;
	/** @type Boolean */ #onePerScrollTarget;
	/** @type String | null */ #listClass;
	/** @type String */ #listType;
	/** @type Object */ #index;

	connectedCallback() {
		super.connectedCallback();

		this.#nested = this.hasAttribute("nested");
		this.#after = this.hasAttribute("after");
		this.#level = this.getAttribute("level") || "h2,h3";
		this.#heading = this.getAttribute("heading");
		this.#headingType = this.getAttribute("heading-type") || "p";
		this.#target = this.getAttribute("target") || "main";
		this.#scrollTarget = this.getAttribute("scroll-target");
		this.#listClass = this.getAttribute("list-class");
		this.#listType = this.getAttribute("list-type") || "ul";
		this.#onePerScrollTarget = this.hasAttribute("one-per-scroll-target");
		this.#index = 0;

		if (!this.render()) this.remove();
	}

	render() {
		// get matching headings
		const target = this.#target ? document.querySelector(this.#target) : document;
		let headings = target?.querySelectorAll(`:is(${this.#level})`);
		// Only consider headings after the ToC element
		if (this.#after)
			headings = Array.from(headings).filter(
				(heading) => heading.compareDocumentPosition(this) & Node.DOCUMENT_POSITION_PRECEDING,
			);
		// If one-per-scroll-target is set, filter headings to one per scroll target
		if (this.#onePerScrollTarget && this.#scrollTarget && headings?.length) {
			const seen = new Set();
			headings = Array.from(headings).filter((heading) => {
				const scrollTarget = heading.closest(this.#scrollTarget);
				if (!scrollTarget) return false;
				if (seen.has(scrollTarget)) return false;
				seen.add(scrollTarget);
				return true;
			});
		}
		if (!headings?.length) return;

		this.innerHTML = this.#createList(headings, true);

		return true;
	}

	/**
	 * Create the list HTML
	 * Runs recursively on nested ToCs
	 * @param  {NodeList} headings The headings to generate the list from
	 * @param  {Boolean}  isFirst  If true, this is the start of the list
	 * @return {String}            The HTML string
	 */
	#createList(headings, isFirst = false) {
		this.#index = isFirst ? 0 : this.#index + 1;

		let list = "";
		for (; this.#index < headings.length; this.#index++) {
			const heading = /** @type {Element} */ (headings[this.#index]);

			let scrollTarget = heading;
			if (this.#scrollTarget) {
				scrollTarget = heading.closest(this.#scrollTarget);
				if (!scrollTarget) continue;
			}

			scrollTarget.id ||= generateStableId(heading.textContent || "");

			const currentLevel = Number(heading.tagName[1]);

			list += /*html*/ `<li>
					<a class="link-subtle" href="#${scrollTarget.id}">${heading.textContent}</a>
					${this.#nested && /** @type {Element} */ (headings[this.#index + 1]?.tagName.slice(1) || currentLevel) > currentLevel ? this.#createList(headings) : ""}
				</li>`;

			// If next heading is bigger, finish this list
			if (
				!isFirst &&
				/** @type {Element} */ (headings[this.#index + 1]?.tagName.slice(1) || currentLevel) <
					currentLevel
			)
				break;
		}

		const renderHeading = isFirst && this.#heading;

		return /*html*/ `
			${renderHeading && this.#headingType !== "li" ? `<${this.#headingType}>${this.#heading}</${this.#headingType}>` : ""}
			<${this.#listType} ${this.#listClass ? `class="${this.#listClass}"` : ""}>
				${renderHeading && this.#headingType === "li" ? `<li><strong>${this.#heading}</strong></li>` : ""}
				${list}
			</${this.#listType}>`;
	}
}

export class SectionToc extends TOC {
	static tagName = "section-toc";
	static {
		customElements.define(SectionToc.tagName, SectionToc);
	}

	#sectionObserver;
	#resizeObserver;
	#visibleSections = new Map();

	connectedCallback() {
		super.connectedCallback();

		this.#sectionObserver = new IntersectionObserver(
			(entries) => {
				// Update visibility data for all observed entries
				entries.forEach((entry) => {
					const id = entry.target.id;
					if (entry.isIntersecting) {
						this.#visibleSections.set(id, entry.intersectionRatio);
					} else {
						this.#visibleSections.delete(id);
					}
				});

				// Find the most visible section
				let mostVisibleId = null;
				let maxRatio = 0;

				for (const [id, ratio] of this.#visibleSections.entries()) {
					if (ratio > maxRatio) {
						maxRatio = ratio;
						mostVisibleId = id;
					}
				}

				// Update all links: only the most visible one gets aria-current="true"
				for (const link of this.querySelectorAll("a[href^='#']")) {
					const id = link.getAttribute("href")?.slice(1);
					if (id) {
						link.setAttribute("aria-current", id === mostVisibleId ? "true" : "false");
					}
				}
			},
			{
				root: null,
				threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
			},
		);

		for (const section of this.querySelectorAll("a[href^='#']")) {
			const id = section.getAttribute("href")?.slice(1);
			if (id) {
				const target = document.getElementById(id);
				if (target) this.#sectionObserver.observe(target);
			}
		}

		// Observe size changes to update height dynamically
		this.#resizeObserver = new ResizeObserver(() => {
			this.#updateHeight();
		});
		this.#resizeObserver.observe(this);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#sectionObserver.disconnect();
		this.#visibleSections.clear();
	}

	/**
	 * Update the CSS custom property for navigation height
	 */
	#updateHeight() {
		document.documentElement.style.setProperty(
			`--${SectionToc.tagName}-height`,
			`${this.offsetHeight}px`,
		);
	}
}
